/*

@@author        j0Shi
@@date          2019.11.08
@@desc          Helps with device specific media queries
                Automatically tracks them and features a reactive bootstrap breakpoint var

*/

import { computed, ref } from '@vue/composition-api';

// save last known breakpoint because breakpoint can be undefined and then we'll simply use the last known one to not run into undefined trouble
let lastKnownBreakpoint = '';
let installed = false;

// predefined media queries
const mediaQueryStrings = ref({
  xs: '(max-width: 576px), (max-width: 575.98px) and (max-height: 575.98px) and (orientation: landscape)',
  sm: '(min-width: 576px) and (max-width: 767.98px), (max-width: 767.98px) and (min-height: 576px) and (max-height: 767.98px) and (orientation: landscape)',
  smIOS: '(min-width: 576px) and (max-width: 895.98px) and (-webkit-device-pixel-ratio: 3), (min-height: 576px) and (max-height: 895.98px) and (orientation: landscape) and (-webkit-device-pixel-ratio: 3)',
  md: '(min-width: 768px) and (max-width: 991.98px)',
  lg: '(min-width: 992px) and (max-width: 1199.98px)',
  xl: '(min-width: 1200px)',
  landscape: '(orientation: landscape)',
  portrait: '(orientation: portrait)',
  pointerMouse: '(pointer: fine)',
  pointerTouch: '(pointer: coarse)',
});

// objects that holds information we are going to need
const mediaQueryObjects = ref({});
const mediaMatches = ref({});

// computed property that holds the current breakpoint
const breakpoint = computed(() => {
  const idents = ['xs', 'sm', 'md', 'lg', 'xl'];
  const match = Object.keys(mediaMatches.value).find(
    (el) => idents.includes(el) && mediaMatches.value[el],
  );
  if (match) {
    lastKnownBreakpoint = match;
    return match;
  }
  return lastKnownBreakpoint;
});

// simple ident getters based on a query string
const getIdent = (query) => {
  const filter = Object.keys(mediaQueryStrings.value).find(
    (el) => mediaQueryStrings.value[el] === query,
  );
  if (typeof filter !== 'undefined') return filter;
  return false;
};

// media query listener, writes matches into mediaMatches object
const listener = (e) => {
  // e.media = used query string
  const ident = getIdent(e.media);
  mediaMatches.value = { ...mediaMatches.value, [ident]: e.matches };
};

// sets up a query using an ident located in mediaQueryStrings
const setupQuery = (ident) => {
  const query = mediaQueryStrings.value[ident];
  mediaQueryObjects.value = { ...mediaQueryObjects.value, [ident]: window.matchMedia(query) };
  // save media string back to mediaQueryStrings because matchMedia might change it
  mediaQueryStrings.value[ident] = mediaQueryObjects.value[ident].media;
  mediaQueryObjects.value[ident].addListener(listener);
  listener(mediaQueryObjects.value[ident]);
};

// set up the predefined queries
const setupMediaQueries = () => {
  // check for window so we can use this in created
  if (typeof window === 'undefined') return;
  if (installed) return;
  // cycle through strings and add media query objects
  Object.keys(mediaQueryStrings.value).forEach((ident) => {
    setupQuery(ident);
  });
  installed = true;
};

// clean up after destroy
const killMediaListeners = () => {
  // cycle through strings and unregister media query listeners
  Object.keys(mediaQueryObjects.value).forEach((ident) => {
    mediaQueryObjects.value[ident].removeListener(listener);
  });
  installed = false;
};

// adds or modifies an existing query
const addMediaQuery = (ident, queryString) => {
  if (typeof mediaQueryStrings.value[ident] !== 'undefined') {
    mediaQueryObjects.value[ident].removeListener(listener);
  }
  mediaQueryStrings.value = { ...mediaQueryStrings.value, [ident]: queryString };
  setupQuery(ident);
};

// method returning the matching state of a specific ident
const isMatching = (ident) => {
  if (typeof mediaMatches.value[ident] === 'undefined') return false;
  return mediaMatches.value[ident];
};

export {
  breakpoint, addMediaQuery, isMatching, setupMediaQueries, killMediaListeners,
};
